import { Injectable } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { includes } from 'lodash';

import { Logger } from './logger.service';
import * as enUS from '../../translations/en-US.json';
import * as frFR from '../../translations/fr-FR.json';
import * as esES from '../../translations/es-ES.json';
import * as deDE from '../../translations/de-DE.json';
import * as itIT from '../../translations/it-IT.json';
import * as ruRU from '../../translations/ru-RU.json';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

const log = new Logger('I18nService');
const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param {string} s The string to extract for translation.
 * @return {string} The same string.
 */
export function extract(s: string) {
  return s;
}

@Injectable()
export class I18nService {

  defaultLanguage: string;
  supportedLanguages: any[];
  private staticDshbrdrefreshGraphCallSource = new Subject<any>();
  // Observable string streams
  componentMethodCalled$ = this.staticDshbrdrefreshGraphCallSource.asObservable();

  constructor(private readonly translateService: TranslateService, private router: Router) {
    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('fr-FR', frFR);
    translateService.setTranslation('es-ES', esES);
    translateService.setTranslation('de-DE', deDE);
    translateService.setTranslation('it-IT', itIT);
    translateService.setTranslation('ru-RU', ruRU);
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param {!string} defaultLanguage The default language to use.
   * @param {Array.<String>} supportedLanguages The list of supported languages.
   */
  init(defaultLanguage: string, supportedLanguages: any[]) {
    this.defaultLanguage = defaultLanguage;
    this.supportedLanguages = supportedLanguages;
    this.language = '';

    this.translateService.onLangChange
      .subscribe((event: LangChangeEvent) => { localStorage.setItem(languageKey, event.lang); });
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param {string} language The IETF language code to set.
   */
  set language(language: string) {

    language = language || localStorage.getItem('language'); //|| this.translateService.getBrowserCultureLang()

    let isSupportedLanguage = includes(this.supportedLanguages.map((item: any) => item.code), language);

    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language = this.supportedLanguages.find(supportedLanguage => supportedLanguage.code.startsWith(language)) || '';
      isSupportedLanguage = Boolean(language);
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this.defaultLanguage;
    }

    this.translateService.use(language);
    var href = this.router.url;
    if (href == "/statistics") {
      this.callComponentMethod();
    }
  }

  // Service message commands
  callComponentMethod() {
    this.staticDshbrdrefreshGraphCallSource.next(true);
  }
  /**
   * Gets the current language.
   * @return {string} The current language code.
   */
  get language(): string {
    return this.translateService.currentLang;
  }

}
